import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import WalletPill from "@/sections/WalletPill";
import UserMenu from "@/sections/UserMenu";
import ComfortToggle from "@/components/ComfortToggle";

const NAV = [
  { to: "/explore", label: "Explore" },
  { to: "/verified", label: "Verified" },
  { to: "/pricing", label: "Pricing" },
];
const MOBILE_NAV = [...NAV, { to: "/leaderboard", label: "Leaderboard" }];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 backdrop-blur-xl border-b transition-colors ${
        scrolled || pathname !== "/" ? "bg-[#050505]/85 border-[#3a3a3a]" : "bg-transparent border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
          <img
            src="/solspur-mark-128.png"
            alt="SolSpur"
            className="w-8 h-8"
            width={32}
            height={32}
          />
          <span className="font-display text-xl font-extrabold tracking-tight">SolSpur</span>
        </Link>
        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={`font-mono text-xs uppercase tracking-widest transition-colors ${
                pathname === n.to ? "text-[#cfff04]" : "text-[#e2e2e2] hover:text-[#cfff04]"
              }`}
            >
              {n.label}
            </Link>
          ))}
        </nav>
        <div className="hidden lg:flex items-center gap-3">
          <ComfortToggle testId="comfort-toggle" />
          <Link
            to="/create"
            data-testid="cta-header-create"
            className="btn-brutal bg-[#cfff04] text-[#050505] font-bold uppercase tracking-wider text-xs px-5 py-3 whitespace-nowrap"
          >
            Launch Coin
          </Link>
          <WalletPill />
          <UserMenu />
        </div>
        <div className="lg:hidden flex items-center gap-2">
          <ComfortToggle testId="comfort-toggle-mobile" />
          <button
            className="p-2"
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            aria-label="menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden border-t border-[#3a3a3a] bg-[#050505]">
          <div className="px-6 py-4 grid gap-3">
            {MOBILE_NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                className="font-mono text-sm uppercase tracking-widest text-[#e2e2e2]"
                onClick={() => setOpen(false)}
              >
                {n.label}
              </Link>
            ))}
            <Link
              to="/create"
              data-testid="mobile-cta-create"
              className="bg-[#cfff04] text-[#050505] font-bold uppercase tracking-wider text-xs px-5 py-3 text-center mt-2"
              onClick={() => setOpen(false)}
            >
              Launch Coin
            </Link>
            <div className="mt-2 flex items-center gap-3 flex-wrap">
              <UserMenu />
              <WalletPill />
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
